import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPublicBaseUrl } from '@/lib/publicBaseUrl';
import { products, Product } from '@/data/pricing';
import { HomePage } from '@/pages/HomePage';
import { PractitionerCertificationPage } from '@/pages/PractitionerCertificationPage';
import { TeamFacilitatorCertificationPage } from '@/pages/TeamFacilitatorCertificationPage';
import { CohortsPage } from '@/pages/CohortsPage';
import { CohortDetailPage } from '@/pages/CohortDetailPage';
import { TeamEffectivenessPage } from '@/pages/TeamEffectivenessPage';
import { TeamMaturityAssessmentPage } from '@/pages/TeamMaturityAssessmentPage';
import { DiagnosticPage } from '@/pages/DiagnosticPage';
import { MiniAssessmentPage } from '@/pages/MiniAssessmentPage';
import { PricingPage } from '@/pages/PricingPage';
import { PrivacyPolicyPage } from '@/pages/PrivacyPolicyPage';
import { TermsOfServicePage } from '@/pages/TermsOfServicePage';

// Lazy-loaded private/admin/portal/results pages (kept out of initial public bundle)
const DiagnosticResultsPage = React.lazy(() => import('@/pages/DiagnosticResultsPage').then(m => ({ default: m.DiagnosticResultsPage })));
const MiniAssessmentResultsPage = React.lazy(() => import('@/pages/MiniAssessmentResultsPage').then(m => ({ default: m.MiniAssessmentResultsPage })));
const AdminPage = React.lazy(() => import('@/pages/AdminPage').then(m => ({ default: m.AdminPage })));
const AdminUsersPage = React.lazy(() => import('@/pages/AdminUsersPage').then(m => ({ default: m.AdminUsersPage })));
const AdminLinksPage = React.lazy(() => import('@/pages/AdminLinksPage'));
const AdminPractitionersPage = React.lazy(() => import('@/pages/AdminPractitionersPage').then(m => ({ default: m.AdminPractitionersPage })));
const AdminTrustLogosPage = React.lazy(() => import('@/pages/AdminTrustLogosPage'));
const AdminSEOManagerPage = React.lazy(() => import('@/pages/AdminSEOManagerPage'));
const AdminTrackingPage = React.lazy(() => import('@/pages/AdminTrackingPage'));
const AdminPaymentSettingsPage = React.lazy(() => import('@/pages/AdminPaymentSettingsPage'));
const AdminTestPaymentsPage = React.lazy(() => import('@/pages/AdminTestPaymentsPage').then(m => ({ default: m.AdminTestPaymentsPage })));
const AdminMailchimpPage = React.lazy(() => import('@/pages/AdminMailchimpPage').then(m => ({ default: m.AdminMailchimpPage })));
const AdminApiQuotasPage = React.lazy(() => import('@/pages/AdminApiQuotasPage').then(m => ({ default: m.AdminApiQuotasPage })));
const AdminCalendarPage = React.lazy(() => import('@/pages/AdminCalendarPage').then(m => ({ default: m.AdminCalendarPage })));
const PaymentSuccessPage = React.lazy(() => import('@/pages/PaymentSuccessPage').then(m => ({ default: m.PaymentSuccessPage })));
const PractitionerDashboardPage = React.lazy(() => import('@/pages/PractitionerDashboardPage').then(m => ({ default: m.PractitionerDashboardPage })));
const PractitionerLoginPage = React.lazy(() => import('@/pages/PractitionerLoginPage').then(m => ({ default: m.PractitionerLoginPage })));
const AdminRegionalContentPage = React.lazy(() => import('@/pages/AdminRegionalContentPage'));
const AdminBlogPage = React.lazy(() => import('@/pages/AdminBlogPage').then(m => ({ default: m.AdminBlogPage })));
const AdminTestimonialsPage = React.lazy(() => import('@/pages/AdminTestimonialsPage').then(m => ({ default: m.AdminTestimonialsPage })));
const CommissionDashboardPage = React.lazy(() => import('@/pages/CommissionDashboardPage').then(m => ({ default: m.CommissionDashboardPage })));
import { RegistrationPage } from '@/pages/RegistrationPage';
import { BlogPage } from '@/pages/BlogPage';
import { BlogPostPage } from '@/pages/BlogPostPage';
import { ContactPage } from '@/pages/ContactPage';
import { AboutPage } from '@/pages/AboutPage';
import { FrameworkPage } from '@/pages/FrameworkPage';
import { ForIndividualsPage } from '@/pages/ForIndividualsPage';
import { OrganizationsPage } from '@/pages/OrganizationsPage';
import { ResourceLibraryPage } from '@/pages/ResourceLibraryPage';
import { WhitepaperChangeFatiguePage } from '@/pages/WhitepaperChangeFatiguePage';
import { WhitepaperExecutiveMisalignmentPage } from '@/pages/WhitepaperExecutiveMisalignmentPage';
import { ScenarioHealthcareSystemsPage } from '@/pages/ScenarioHealthcareSystemsPage';
import { ScenarioGlobalLogisticsPage } from '@/pages/ScenarioGlobalLogisticsPage';
import { ToolkitPersonalMasteryPage } from '@/pages/ToolkitPersonalMasteryPage';
import { ToolkitTeamAlignmentPage } from '@/pages/ToolkitTeamAlignmentPage';
import { PractitionersPage } from '@/pages/PractitionersPage';
import { PractitionerDirectoryPage } from '@/pages/PractitionerDirectoryPage';
import { LensEnneagramPage } from '@/pages/LensEnneagramPage';
import { LensPersonalMasteryPage } from '@/pages/LensPersonalMasteryPage';
import { LensEmotionalResiliencePage } from '@/pages/LensEmotionalResiliencePage';
import { LensSocialDrivesPage } from '@/pages/LensSocialDrivesPage';
import { LensEnergyCentresPage } from '@/pages/LensEnergyCentresPage';
import { EvidencePage } from '@/pages/EvidencePage';
import { ICFExplainerPage } from '@/pages/ICFExplainerPage';
import { ExecutiveCoachingPage } from '@/pages/ExecutiveCoachingPage';
import { StandardReportPage } from './pages/StandardReportPage';
import { PremiumReportPage } from './pages/PremiumReportPage';
import { TeamReportPage } from './pages/TeamReportPage';
import { TeamLeaderReportPage } from './pages/TeamLeaderReportPage';
import { PersonalMasteryProductPage } from '@/pages/PersonalMasteryProductPage';
import { PractitionerProfilePage } from '@/pages/PractitionerProfilePage';
import { PractitionerOnboardingPage } from '@/pages/PractitionerOnboardingPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { CampaignLandingPage } from '@/pages/CampaignLandingPage';
import {
  EnneagramType1Page,
  EnneagramType2Page,
  EnneagramType3Page,
  EnneagramType4Page,
  EnneagramType5Page,
  EnneagramType6Page,
  EnneagramType7Page,
  EnneagramType8Page,
  EnneagramType9Page,
  ENNEAGRAM_TYPES_DATA
} from '@/pages/enneagram/EnneagramTypePages';

// Feature flag for practitioner directory routes (defaults to OFF).
// NOTE: import.meta is guarded for client/Vite builds. In the Node CJS server bundle
// import.meta resolves undefined, keeping this disabled server-side.
// When enabling the directory in production, ensure the server runtime reads
// process.env.VITE_ENABLE_PRACTITIONER_DIRECTORY or process.env.ENABLE_PRACTITIONER_DIRECTORY.
export const isPractitionerDirectoryEnabled = true;

export interface RouteConfig {
  path: string;
  component: React.ComponentType<any>;
  publiclyIndexable: boolean;
  seo: {
    title: string;
    description: string;
    schema?: Record<string, any>;
    byLocale?: {
      en_ZA?: { title?: string; description?: string; schema?: Record<string, any> };
      en_GB?: { title?: string; description?: string; schema?: Record<string, any> };
      en_US?: { title?: string; description?: string; schema?: Record<string, any> };
    };
  };
  /** Redundant for hreflang; preserved as source for the .co.za redirect map */
  coZaLegacySlug?: string;
  standalone?: boolean;
  disabled?: boolean;
}

const createCourseSchema = (
  product: Product,
  description: string,
  options?: { courseMode?: string }
) => ({
  "@context": "https://schema.org",
  "@type": "Course",
  "name": product.name,
  "description": description,
  "provider": {
    "@type": "Organization",
    "name": "FIVE LENS Enneagram People Development",
    "sameAs": getPublicBaseUrl()
  },
  ...(options?.courseMode ? { courseMode: options.courseMode } : {}),
  "offers": [
    {
      "@type": "Offer",
      "price": product.zar.amountExclVat.toString(),
      "priceCurrency": "ZAR",
      "availability": "https://schema.org/InStock",
      "url": `${getPublicBaseUrl()}${product.id === 'practitioner-certification' ? '/practitioner-certification' : '/certification/team-facilitator'}`
    },
    {
      "@type": "Offer",
      "price": (product.gbp?.amount || (product.id === 'practitioner-certification' ? 900 : 1350)).toString(),
      "priceCurrency": "GBP",
      "availability": "https://schema.org/InStock",
      "url": `${getPublicBaseUrl()}${product.id === 'practitioner-certification' ? '/practitioner-certification' : '/certification/team-facilitator'}`
    },
    {
      "@type": "Offer",
      "price": (product.usd.amount || (product.id === 'practitioner-certification' ? 1200 : 1800)).toString(),
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
      "url": `${getPublicBaseUrl()}${product.id === 'practitioner-certification' ? '/practitioner-certification' : '/certification/team-facilitator'}`
    }
  ]
});

const practitionerCourseSchema = () =>
  createCourseSchema(
    products['practitioner-certification'],
    "Comprehensive Enneagram Practitioner Certification combining vertical leadership maturity and multidimensional behavioral assessment.",
    { courseMode: "online" }
  );

const teamFacilitatorCourseSchema = () =>
  createCourseSchema(
    products['team-facilitator-certification'],
    "Facilitate team sessions using modules on trust, feedback and collaboration with the FIVE LENS Enneagram framework."
  );



export const routes: RouteConfig[] = [
  {
    path: '/',
    component: HomePage,
    publiclyIndexable: true,
    seo: {
      title: 'FIVE LENS Enneagram | People Development & Team Effectiveness',
      description: 'Resolve team friction at its root. The FIVE LENS framework integrates the Enneagram with four psychological lenses for data-driven coaching and leadership development.',
      byLocale: {
        en_ZA: {
          title: 'FIVE LENS Enneagram | People Development & Team Effectiveness | South Africa',
          description: 'Resolve team friction at its root. FIVE LENS Enneagram coaching and certification for South African coaches, HR leaders and executive teams. Johannesburg and Cape Town.',
        },
        en_GB: {
          title: 'FIVE LENS Enneagram | People Development & Team Effectiveness | UK',
          description: 'Resolve team friction at its root. FIVE LENS Enneagram coaching and certification for UK coaches, HR leaders and executive teams. Delivered live online.',
        },
        en_US: {
          title: 'FIVE LENS Enneagram | People Development & Team Effectiveness | USA',
          description: 'Resolve team friction at its root. FIVE LENS Enneagram coaching and certification for US coaches, HR leaders and executive teams. Greater Philadelphia and nationwide.',
        },
      },
      schema: {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": "FIVE LENS Enneagram People Development",
        "image": `${getPublicBaseUrl()}/og-image.jpg`,
        "url": getPublicBaseUrl(),
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "ZA"
        },
        "sameAs": [
          "https://www.linkedin.com/company/fivelens"
        ]
      }
    },
    coZaLegacySlug: ''
  },
  {
    path: '/practitioner-certification',
    component: PractitionerCertificationPage,
    publiclyIndexable: true,
    seo: {
      title: 'Enneagram Practitioner Certification | ICF 23.75 CCE | FIVE LENS',
      description: 'Become a certified FIVE LENS Enneagram practitioner. ICF-accredited (23.75 CCE), 8 weeks blended online learning. Debrief behavioural profiles with confidence.',
      schema: practitionerCourseSchema(),
      byLocale: {
        en_ZA: {
          title: 'Enneagram Practitioner Certification | ICF 23.75 CCE | South Africa | FIVE LENS',
          description: 'Become an accredited FIVE LENS Enneagram Practitioner. ICF 23.75 CCE, blended online learning, six live sessions in SAST.',
          schema: practitionerCourseSchema(),
        },
        en_GB: {
          title: 'Enneagram Practitioner Certification | ICF 23.75 CCE | UK | FIVE LENS',
          description: 'Become an accredited FIVE LENS Enneagram Practitioner. ICF 23.75 CCE, blended online learning, six live sessions.',
        },
        en_US: {
          title: 'Enneagram Practitioner Certification | ICF 23.75 CCE | USA | FIVE LENS',
          description: 'Become an accredited FIVE LENS Enneagram Practitioner. ICF 23.75 CCE, blended online learning, six live sessions.',
        },
      },
    },
    coZaLegacySlug: 'practitioner-certification'
  },
  {
    path: '/certification/team-facilitator',
    component: TeamFacilitatorCertificationPage,
    publiclyIndexable: true,
    seo: {
      title: 'Team Facilitator Certification | Enneagram Team Coaching | FIVE LENS',
      description: 'Facilitate team-building sessions using the FIVE LENS framework. Level 2 certification with customisable Team Skills Modules. 4 weeks online.',
      schema: teamFacilitatorCourseSchema(),
      byLocale: {
        en_ZA: {
          title: 'Team Facilitator Certification | Enneagram Team Coaching | South Africa | FIVE LENS',
          description: 'Level 2 FIVE LENS Team Facilitator Certification. Deliver team dynamics mapping and trust workshops.',
          schema: teamFacilitatorCourseSchema(),
        },
        en_GB: {
          title: 'Team Facilitator Certification | Enneagram Team Coaching | UK | FIVE LENS',
          description: 'Level 2 FIVE LENS Team Facilitator Certification. Deliver team dynamics mapping and trust workshops.',
        },
        en_US: {
          title: 'Team Facilitator Certification | Enneagram Team Coaching | USA | FIVE LENS',
          description: 'Level 2 FIVE LENS Team Facilitator Certification. Deliver team dynamics mapping and trust workshops.',
        },
      },
    }
  },
  {
    path: '/cohorts',
    component: CohortsPage,
    publiclyIndexable: true,
    seo: {
      title: 'Upcoming Certification Cohorts | FIVE LENS Enneagram',
      description: 'View upcoming Practitioner and Team Facilitator certification dates. Enrol online — live Zoom sessions with expert faculty. Limited spots per cohort.'
    }
  },
  {
    path: '/cohorts/:slug',
    component: CohortDetailPage,
    publiclyIndexable: true,
    seo: {
      title: 'Certification Cohort | FIVE LENS Enneagram',
      description: 'View schedule, curriculum and enrolment details for this FIVE LENS certification cohort.'
    }
  },
  {
    path: '/team-effectiveness',
    component: TeamEffectivenessPage,
    publiclyIndexable: true,
    seo: {
      title: 'Team Effectiveness Programme | Enneagram Team Assessment | FIVE LENS',
      description: 'Diagnose and resolve systemic team friction with FIVE LENS. Measurable improvements in cross-functional collaboration, trust, and decision-making.',
      byLocale: {
        en_ZA: {
          title: 'Team Effectiveness Programme | Enneagram Team Assessment | South Africa | FIVE LENS',
        },
        en_GB: {
          title: 'Team Effectiveness Programme | Enneagram Team Assessment | UK | FIVE LENS',
        },
        en_US: {
          title: 'Team Effectiveness Programme | Enneagram Team Assessment | USA | FIVE LENS',
        },
      },
    },
    coZaLegacySlug: 'team-effectiveness'
  },
  {
    path: '/team-maturity-assessment',
    component: TeamMaturityAssessmentPage,
    publiclyIndexable: true,
    seo: {
      title: 'Team Maturity Assessment | Free Organisational Diagnostic | FIVE LENS',
      description: "Assess your team's behavioural maturity across five psychological dimensions. Data-driven insights for targeted leadership and team development."
    }
  },
  {
    path: '/diagnostic',
    component: DiagnosticPage,
    publiclyIndexable: true,
    seo: {
      title: 'Free Team Friction Diagnostic | 3-Minute Assessment | FIVE LENS',
      description: 'Take the free 3-minute diagnostic to identify the core psychological drivers causing silos, burnout, and decision paralysis in your organisation.'
    }
  },
  {
    path: '/diagnostic-results',
    component: DiagnosticResultsPage,
    publiclyIndexable: false,
    seo: {
      title: 'Diagnostic Results | FIVE LENS Enneagram',
      description: 'View your personal assessment and diagnostic results.'
    }
  },
  {
    path: '/diagnostic/results',
    component: DiagnosticResultsPage,
    publiclyIndexable: false,
    seo: {
      title: 'Diagnostic Results | FIVE LENS Enneagram',
      description: 'View your personal assessment and diagnostic results.'
    }
  },
  {
    path: '/mini-assessment',
    component: MiniAssessmentPage,
    publiclyIndexable: true,
    seo: {
      title: 'Free 4-Minute Enneagram Mini Assessment | FIVE LENS',
      description: 'Answer 24 quick questions to see the pattern you lean on under pressure, where you go first, and a resilience snapshot. Free, about 4 minutes.',
      byLocale: {
        en_ZA: {
          title: 'Free 4-Minute Enneagram Mini Assessment | South Africa | FIVE LENS',
          description: 'Answer 24 quick questions to see the pattern you lean on under pressure, where you go first, and a resilience snapshot. Free, about 4 minutes.'
        },
        en_GB: {
          title: 'Free 4-Minute Enneagram Mini Assessment | UK | FIVE LENS',
          description: 'Answer 24 quick questions to see the pattern you lean on under pressure, where you go first, and a resilience snapshot. Free, about 4 minutes.'
        },
        en_US: {
          title: 'Free 4-Minute Enneagram Mini Assessment | USA | FIVE LENS',
          description: 'Answer 24 quick questions to see the pattern you lean on under pressure, where you go first, and a resilience snapshot. Free, about 4 minutes.'
        }
      }
    }
  },
  {
    path: '/mini-assessment/results',
    component: MiniAssessmentResultsPage,
    publiclyIndexable: false,
    seo: {
      title: 'Your Mini Assessment Results | FIVE LENS',
      description: 'Your personal FIVE LENS Enneagram pattern snapshot and resilience summary.'
    }
  },
  {
    path: '/mini-assessment-results',
    component: MiniAssessmentResultsPage,
    publiclyIndexable: false,
    seo: {
      title: 'Your Mini Assessment Results | FIVE LENS',
      description: 'Your personal FIVE LENS Enneagram pattern snapshot and resilience summary.'
    }
  },
  {
    path: '/pricing',
    component: PricingPage,
    publiclyIndexable: true,
    seo: {
      title: 'Certification Pricing | Practitioner & Team Facilitator | FIVE LENS',
      description: 'Transparent pricing for FIVE LENS certifications. Practitioner Certification, Team Facilitator Certification, and combined bundle options. ICF accredited.',
      byLocale: {
        en_ZA: {
          title: 'Certification Pricing | Practitioner & Team Facilitator | South Africa | FIVE LENS',
          description: 'Transparent pricing for FIVE LENS certifications in South Africa. Practitioner, Team Facilitator, and combined bundle options. ICF accredited.',
        },
        en_GB: {
          title: 'Certification Pricing | Practitioner & Team Facilitator | UK | FIVE LENS',
          description: 'Transparent pricing for FIVE LENS certifications in the UK. Practitioner, Team Facilitator, and combined bundle options. ICF accredited.',
        },
        en_US: {
          title: 'Certification Pricing | Practitioner & Team Facilitator | USA | FIVE LENS',
          description: 'Transparent pricing for FIVE LENS certifications. Practitioner, Team Facilitator, and combined bundle options. ICF accredited.',
        },
      },
    },
    coZaLegacySlug: 'pricing'
  },
  {
    path: '/privacy-policy',
    component: PrivacyPolicyPage,
    publiclyIndexable: true,
    seo: {
      title: 'Privacy Policy | FIVE LENS Enneagram',
      description: 'How FIVE LENS Enneagram collects, uses, and protects your personal information. Read our full privacy policy.'
    }
  },
  {
    path: '/terms-of-service',
    component: TermsOfServicePage,
    publiclyIndexable: true,
    seo: {
      title: 'Terms of Service | FIVE LENS Enneagram',
      description: 'Terms and conditions governing use of the FIVE LENS Enneagram website, certifications, and assessment services.'
    }
  },
  {
    path: '/admin/login',
    component: AdminPage,
    publiclyIndexable: false,
    seo: {
      title: 'Admin Sign In | FIVE LENS Enneagram',
      description: 'Administration sign in portal.'
    }
  },
  {
    path: '/admin',
    component: AdminPage,
    publiclyIndexable: false,
    seo: {
      title: 'Admin Portal | FIVE LENS Enneagram',
      description: 'Administration management portal.'
    }
  },
  {
    path: '/admin/users',
    component: AdminUsersPage,
    publiclyIndexable: false,
    seo: {
      title: 'Admin Users | FIVE LENS Enneagram',
      description: 'User administration.'
    }
  },
  {
    path: '/admin/links',
    component: AdminLinksPage,
    publiclyIndexable: false,
    seo: {
      title: 'Admin Links | FIVE LENS Enneagram',
      description: 'Manage external site links.'
    }
  },
  {
    path: '/admin/practitioners',
    component: AdminPractitionersPage,
    publiclyIndexable: false,
    seo: {
      title: 'Admin Practitioners | FIVE LENS Enneagram',
      description: 'Manage practitioner directory.'
    }
  },
  {
    path: '/admin/trust-logos',
    component: AdminTrustLogosPage,
    publiclyIndexable: false,
    seo: {
      title: 'Admin Trust Logos | FIVE LENS Enneagram',
      description: 'Manage brand client trust logos.'
    }
  },
  {
    path: '/admin/seo-manager',
    component: AdminSEOManagerPage,
    publiclyIndexable: false,
    seo: {
      title: 'Admin SEO Manager | FIVE LENS Enneagram',
      description: 'Manage page SEO and social share metadata.'
    }
  },
  {
    path: '/admin/tracking',
    component: AdminTrackingPage,
    publiclyIndexable: false,
    seo: {
      title: 'Admin Tracking | FIVE LENS Enneagram',
      description: 'Manage analytics and marketing scripts.'
    }
  },
  {
    path: '/admin/mailchimp',
    component: AdminMailchimpPage,
    publiclyIndexable: false,
    seo: {
      title: 'Mailchimp Integration | FIVE LENS Enneagram',
      description: 'Manage newsletter subscribers and Mailchimp audience synchronization.'
    }
  },
  {
    path: '/admin/quotas',
    component: AdminApiQuotasPage,
    publiclyIndexable: false,
    seo: {
      title: 'API Quotas & Limits | FIVE LENS Enneagram',
      description: 'Visual monitoring and early warning alerts for API quota limits across Gemini AI, Firestore, and Mailchimp.'
    }
  },
  {
    path: '/admin/payments',
    component: AdminPaymentSettingsPage,
    publiclyIndexable: false,
    seo: {
      title: 'Admin Payments | FIVE LENS Enneagram',
      description: 'Manage payment gateway configurations.'
    }
  },
  {
    path: '/admin/test-payments',
    component: AdminTestPaymentsPage,
    publiclyIndexable: false,
    seo: {
      title: 'Test Payments | FIVE LENS Enneagram',
      description: 'Run test gateway transactions for PayFast and PayPal.'
    }
  },
  {
    path: '/admin/calendar',
    component: AdminCalendarPage,
    publiclyIndexable: false,
    seo: {
      title: 'Admin Calendar | FIVE LENS Enneagram',
      description: 'View and manage session calendars.'
    }
  },
  {
    path: '/payment/success',
    component: PaymentSuccessPage,
    publiclyIndexable: false,
    seo: {
      title: 'Payment Successful | FIVE LENS Enneagram',
      description: 'Thank you for your order.'
    }
  },
  {
    path: '/practitioner/login',
    component: PractitionerLoginPage,
    publiclyIndexable: false,
    seo: {
      title: 'Practitioner Portal Sign In | FIVE LENS Enneagram',
      description: 'Access the FIVE LENS practitioner portal to manage your profile, bookings, and clients.'
    }
  },
  {
    path: '/practitioner/dashboard',
    component: PractitionerDashboardPage,
    publiclyIndexable: false,
    seo: {
      title: 'Practitioner Dashboard | FIVE LENS Enneagram',
      description: 'Manage your practitioner profile, client enquiries, bookings, and earnings.'
    }
  },
  {
    path: '/admin/regional-content',
    component: AdminRegionalContentPage,
    publiclyIndexable: false,
    seo: {
      title: 'Regional Content Management | FIVE LENS Enneagram',
      description: 'Manage localized copy and region specific overrides.'
    }
  },
  {
    path: '/admin/blog',
    component: AdminBlogPage,
    publiclyIndexable: false,
    seo: {
      title: 'Blog Management | FIVE LENS Enneagram',
      description: 'Draft and publish blog articles.'
    }
  },
  {
    path: '/admin/testimonials',
    component: AdminTestimonialsPage,
    publiclyIndexable: false,
    seo: {
      title: 'Testimonials Management | FIVE LENS Enneagram',
      description: 'Manage customer and client reviews.'
    }
  },
  {
    path: '/register',
    component: RegistrationPage,
    publiclyIndexable: true,
    seo: {
      title: 'Register for Certification | FIVE LENS Enneagram',
      description: 'Enrol in the FIVE LENS Practitioner or Team Facilitator certification programme. Secure your spot in the next cohort.'
    }
  },
  {
    path: '/blog',
    component: BlogPage,
    publiclyIndexable: true,
    seo: {
      title: 'Blog | Enneagram Insights for Coaches & Leaders | FIVE LENS',
      description: 'Articles on team effectiveness, leadership development, Enneagram coaching, and the science of behavioural assessment. Written for coaches, HR leaders, and practitioners.'
    },
    coZaLegacySlug: 'blog'
  },
  {
    path: '/blog/:id',
    component: BlogPostPage,
    publiclyIndexable: true,
    seo: {
      title: 'Blog Article | FIVE LENS Enneagram',
      description: 'In-depth articles and research on the FIVE LENS framework.'
    }
  },
  {
    path: '/contact',
    component: ContactPage,
    publiclyIndexable: true,
    seo: {
      title: 'Contact FIVE LENS Enneagram | Get in Touch',
      description: 'Reach our teams in Cape Town and Greater Philadelphia. Book a discovery call, enquire about certifications, or discuss enterprise solutions.'
    },
    coZaLegacySlug: 'contact-us'
  },
  {
    path: '/about',
    component: AboutPage,
    publiclyIndexable: true,
    seo: {
      title: 'About FIVE LENS Enneagram | Our Story & Team',
      description: 'Meet the team behind FIVE LENS. Founded in South Africa, operating globally — decades of experience in behavioural science, coaching, and organisational development.'
    },
    coZaLegacySlug: 'about-us'
  },
  {
    path: '/framework',
    component: FrameworkPage,
    publiclyIndexable: true,
    seo: {
      title: 'The Five Lens Framework | Beyond Single-Lens Enneagram',
      description: 'Five integrated lenses — Enneagram, Personal Mastery, Emotional Resilience, Social Drives, Energy Centers — for unprecedented depth in coaching and team development.'
    },
    coZaLegacySlug: 'the-five-lenses'
  },

  {
    path: '/for-individuals',
    component: ForIndividualsPage,
    publiclyIndexable: true,
    seo: {
      title: 'Enneagram Coaching for Individuals | Personal Development | FIVE LENS',
      description: 'Discover your core behavioural drives, blind spots, and growth path. Book a one-on-one FIVE LENS Enneagram debrief with an accredited coach.',
      byLocale: {
        en_ZA: {
          title: 'Enneagram Coaching for Individuals | Personal Development | South Africa | FIVE LENS',
        },
        en_GB: {
          title: 'Enneagram Coaching for Individuals | Personal Development | UK | FIVE LENS',
        },
        en_US: {
          title: 'Enneagram Coaching for Individuals | Personal Development | USA | FIVE LENS',
        },
      },
    }
  },
  {
    path: '/organizations',
    component: OrganizationsPage,
    publiclyIndexable: true,
    seo: {
      title: 'Enneagram for Organisations | Team Alignment & Culture Change | FIVE LENS',
      description: 'Deploy data-driven behavioural interventions that align executive teams, break down silos, and turn operational drag into high-trust performance.',
      byLocale: {
        en_ZA: {
          title: 'Enneagram for Organisations | Team Alignment & Culture Change | South Africa | FIVE LENS',
        },
        en_GB: {
          title: 'Enneagram for Organisations | Team Alignment & Culture Change | UK | FIVE LENS',
        },
        en_US: {
          title: 'Enneagram for Organisations | Team Alignment & Culture Change | USA | FIVE LENS',
        },
      },
    },
    coZaLegacySlug: 'for-organisations'
  },
  {
    path: '/resource-library',
    component: ResourceLibraryPage,
    publiclyIndexable: true,
    seo: {
      title: 'Resource Library | Guides & Tools | FIVE LENS Enneagram',
      description: 'Downloadable guides, research papers, and practical tools for coaches and HR professionals using the Enneagram in organisations.'
    }
  },
  {
    path: '/resource-library/navigating-change-fatigue',
    component: WhitepaperChangeFatiguePage,
    publiclyIndexable: true,
    seo: {
      title: 'Navigating Change Fatigue Whitepaper | FIVE LENS Enneagram',
      description: 'Download our comprehensive whitepaper on navigating change fatigue in organizations.'
    }
  },
  {
    path: '/resource-library/the-cost-of-executive-misalignment',
    component: WhitepaperExecutiveMisalignmentPage,
    publiclyIndexable: true,
    seo: {
      title: 'The Cost of Executive Misalignment | FIVE LENS Enneagram',
      description: 'Why leadership teams that agree on paper drift apart in practice, what the published research shows, and a twelve-question audit to find where misalignment is costing you.'
    }
  },
  {
    path: '/resource-library/healthcare-systems-optimisation',
    component: ScenarioHealthcareSystemsPage,
    publiclyIndexable: true,
    seo: {
      title: 'Healthcare Systems Optimisation | FIVE LENS Enneagram',
      description: 'An illustrative scenario: how the Energy Centres lens can help a multidisciplinary clinical team understand recurring friction, what to try and what to measure.'
    }
  },
  {
    path: '/resource-library/transforming-global-logistics',
    component: ScenarioGlobalLogisticsPage,
    publiclyIndexable: true,
    seo: {
      title: 'Transforming Global Logistics | FIVE LENS Enneagram',
      description: 'An illustrative scenario: how a newly combined logistics leadership team could use the FIVE LENS framework to work through departmental friction and slow decisions.'
    }
  },
  {
    path: '/resource-library/personal-mastery-assessment-toolkit',
    component: ToolkitPersonalMasteryPage,
    publiclyIndexable: true,
    seo: {
      title: 'Personal Mastery Assessment Toolkit | FIVE LENS Enneagram',
      description: 'Self-reflection worksheets for leaders, coaches and HR professionals to baseline personal mastery and emotional regulation, with a 30-day development plan.'
    }
  },
  {
    path: '/resource-library/team-alignment-workshop-guide',
    component: ToolkitTeamAlignmentPage,
    publiclyIndexable: true,
    seo: {
      title: 'Team Alignment Workshop Guide | FIVE LENS Enneagram',
      description: 'Step-by-step facilitation guide for running an introductory FIVE LENS alignment workshop with newly formed leadership teams.'
    }
  },
  {
    path: '/practitioners',
    component: isPractitionerDirectoryEnabled ? PractitionersPage : NotFoundPage,
    publiclyIndexable: true,
    disabled: !isPractitionerDirectoryEnabled,
    seo: {
      title: 'For Coaches and Practitioners | FIVE LENS Enneagram Certification',
      description: 'Equip yourself to develop others with the FIVE LENS Enneagram certification, tools and resources.'
    },
    coZaLegacySlug: 'practitioners'
  },
  {
    path: '/practitioner-directory',
    component: isPractitionerDirectoryEnabled ? PractitionerDirectoryPage : NotFoundPage,
    publiclyIndexable: false,
    disabled: !isPractitionerDirectoryEnabled,
    seo: {
      title: 'Find a Certified FIVE LENS Enneagram Coach | Practitioner Directory',
      description: 'Search our global directory of accredited FIVE LENS Enneagram practitioners. Find a certified coach for personal debriefs or team facilitation.'
    }
  },
  {
    path: '/find-a-practitioner',
    component: isPractitionerDirectoryEnabled ? PractitionerDirectoryPage : NotFoundPage,
    publiclyIndexable: false,
    disabled: !isPractitionerDirectoryEnabled,
    seo: {
      title: 'Find a Certified FIVE LENS Enneagram Coach | Practitioner Directory',
      description: 'Search our global directory of accredited FIVE LENS Enneagram practitioners. Find a certified coach for personal debriefs or team facilitation.'
    }
  },
  {
    path: '/practitioner-onboarding',
    component: PractitionerOnboardingPage,
    publiclyIndexable: false,
    seo: {
      title: 'Practitioner Onboarding Application | FIVE LENS Enneagram',
      description: 'Apply to join the FIVE LENS Enneagram certified practitioner network as an accredited coach or corporate partner. Submit your credentials for admin review.'
    }
  },
  {
    path: '/practitioner-apply',
    component: PractitionerOnboardingPage,
    publiclyIndexable: true,
    seo: {
      title: 'Practitioner Application | FIVE LENS Enneagram',
      description: 'Apply to join the FIVE LENS Enneagram certified practitioner network as an accredited coach or corporate partner.'
    }
  },
  {
    path: '/admin/commissions',
    component: CommissionDashboardPage,
    publiclyIndexable: false,
    seo: {
      title: 'Commission Dashboard | FIVE LENS Enneagram',
      description: 'Practitioner commissions and revenue splits.'
    }
  },
  {
    path: '/lens/enneagram',
    component: LensEnneagramPage,
    publiclyIndexable: true,
    seo: {
      title: 'The Enneagram Lens | Core Motivations & Defence Mechanisms | FIVE LENS',
      description: 'Understand core motivations, fears, and internal defence mechanisms that drive behaviour. The foundational lens in the FIVE LENS framework.'
    }
  },
  {
    path: '/lens/personal-mastery',
    component: LensPersonalMasteryPage,
    publiclyIndexable: true,
    seo: {
      title: 'Personal Mastery & Maturity Lens | Vertical Development | FIVE LENS',
      description: 'Measure resilience, self-efficacy, and leadership maturity. The Personal Mastery lens assesses vertical development and capacity for organisational complexity.'
    }
  },
  {
    path: '/lens/emotional-resilience',
    component: LensEmotionalResiliencePage,
    publiclyIndexable: true,
    seo: {
      title: 'Emotional Resilience Lens | Stress Recovery & Adaptability | FIVE LENS',
      description: 'Assess capacity to adapt, recover, and maintain clarity under strain. The Emotional Resilience lens maps stress recovery for coaching and team development.'
    }
  },
  {
    path: '/lens/social-drives',
    component: LensSocialDrivesPage,
    publiclyIndexable: true,
    seo: {
      title: 'Social Drives Lens | Status, Connection & Belonging | FIVE LENS',
      description: 'Analyse how individuals navigate status, connection, and belonging within teams. Uncover fundamental motivations and unmet needs driving team dynamics.'
    }
  },
  {
    path: '/lens/energy-centres',
    component: LensEnergyCentresPage,
    publiclyIndexable: true,
    seo: {
      title: 'Energy Centers Lens | Instinctual, Emotional & Intellectual | FIVE LENS',
      description: 'Explore how individuals process information and make decisions using three core centres. The Energy Centers lens completes the FIVE LENS framework.'
    }
  },
  {
    path: '/evidence-and-research',
    component: EvidencePage,
    publiclyIndexable: true,
    seo: {
      title: 'Evidence & Scientific Validation | FIVE LENS Enneagram Research',
      description: 'Peer-reviewed research, PhD validation, and psychometric data behind the Five Lens Questionnaire. A scientifically grounded approach to the Enneagram.'
    },
    coZaLegacySlug: 'evidence-and-research'
  },
  {
    path: '/icf-cce-points',
    component: ICFExplainerPage,
    publiclyIndexable: true,
    seo: {
      title: 'ICF CCE Points | Enneagram Certification for Coaches | FIVE LENS',
      description: 'Earn 23.75 ICF Continuing Coach Education points with FIVE LENS Practitioner Certification. Recognised by the International Coaching Federation.'
    }
  },
  {
    path: '/executive-coaching',
    component: ExecutiveCoachingPage,
    publiclyIndexable: false,
    seo: {
      title: 'Executive Coaching | FIVE LENS Enneagram',
      description: 'Transformational 1-on-1 coaching for executives, senior leaders, and high-impact teams.'
    }
  },
  {
    path: '/reports/standard',
    component: StandardReportPage,
    publiclyIndexable: false,
    seo: {
      title: 'Standard Personal Feedback Report | FIVE LENS Enneagram',
      description: 'In-depth individual behavioural profile covering all five lenses. Used by certified practitioners for one-on-one coaching debriefs.'
    }
  },
  {
    path: '/reports/premium',
    component: PremiumReportPage,
    publiclyIndexable: false,
    seo: {
      title: 'Premium Personal Feedback Report | FIVE LENS Enneagram',
      description: 'Comprehensive behavioural profile with enhanced team-context insights. Designed for team coaching and organisational development engagements.'
    }
  },
  {
    path: '/reports/team',
    component: TeamReportPage,
    publiclyIndexable: false,
    seo: {
      title: 'Team Report | FIVE LENS Enneagram',
      description: "Map your team's collective behavioural dynamics across all five lenses. Identify friction patterns, communication gaps, and alignment opportunities."
    }
  },
  {
    path: '/reports/team-leader',
    component: TeamLeaderReportPage,
    publiclyIndexable: false,
    seo: {
      title: 'Team Leader Report | FIVE LENS Enneagram',
      description: 'Targeted insights for the team leader — how your style shapes team dynamics, where blind spots create friction, and how to lead more effectively.'
    }
  },
  {
    path: '/personal-mastery-and-resilience',
    component: PersonalMasteryProductPage,
    publiclyIndexable: true,
    seo: {
      title: 'Personal Mastery & Resilience | Leadership Development | FIVE LENS',
      description: 'Build emotional resilience and leadership maturity. The FIVE LENS approach measures vertical development, self-efficacy, and adaptive capacity under pressure.'
    }
  },
  {
    path: '/practitioners/:id',
    component: isPractitionerDirectoryEnabled ? PractitionerProfilePage : NotFoundPage,
    publiclyIndexable: false,
    disabled: !isPractitionerDirectoryEnabled,
    seo: {
      title: 'Practitioner Profile | FIVE LENS Enneagram',
      description: 'Certified practitioner biography and coaching booking information.'
    }
  },
  {
    path: '/campaign/demo',
    component: CampaignLandingPage,
    publiclyIndexable: false,
    standalone: true,
    seo: {
      title: 'Special Preview | FIVE LENS Enneagram',
      description: 'Exclusive preview and demonstration.'
    }
  },
  // Enneagram Type Pages (1–9)
  {
    path: '/enneagram-type-1',
    component: EnneagramType1Page,
    publiclyIndexable: true,
    seo: {
      title: ENNEAGRAM_TYPES_DATA[0].title,
      description: ENNEAGRAM_TYPES_DATA[0].description
    }
  },
  {
    path: '/enneagram-type-2',
    component: EnneagramType2Page,
    publiclyIndexable: true,
    seo: {
      title: ENNEAGRAM_TYPES_DATA[1].title,
      description: ENNEAGRAM_TYPES_DATA[1].description
    }
  },
  {
    path: '/enneagram-type-3',
    component: EnneagramType3Page,
    publiclyIndexable: true,
    seo: {
      title: ENNEAGRAM_TYPES_DATA[2].title,
      description: ENNEAGRAM_TYPES_DATA[2].description
    }
  },
  {
    path: '/enneagram-type-4',
    component: EnneagramType4Page,
    publiclyIndexable: true,
    seo: {
      title: ENNEAGRAM_TYPES_DATA[3].title,
      description: ENNEAGRAM_TYPES_DATA[3].description
    }
  },
  {
    path: '/enneagram-type-5',
    component: EnneagramType5Page,
    publiclyIndexable: true,
    seo: {
      title: ENNEAGRAM_TYPES_DATA[4].title,
      description: ENNEAGRAM_TYPES_DATA[4].description
    }
  },
  {
    path: '/enneagram-type-6',
    component: EnneagramType6Page,
    publiclyIndexable: true,
    seo: {
      title: ENNEAGRAM_TYPES_DATA[5].title,
      description: ENNEAGRAM_TYPES_DATA[5].description
    }
  },
  {
    path: '/enneagram-type-7',
    component: EnneagramType7Page,
    publiclyIndexable: true,
    seo: {
      title: ENNEAGRAM_TYPES_DATA[6].title,
      description: ENNEAGRAM_TYPES_DATA[6].description
    }
  },
  {
    path: '/enneagram-type-8',
    component: EnneagramType8Page,
    publiclyIndexable: true,
    seo: {
      title: ENNEAGRAM_TYPES_DATA[7].title,
      description: ENNEAGRAM_TYPES_DATA[7].description
    }
  },
  {
    path: '/enneagram-type-9',
    component: EnneagramType9Page,
    publiclyIndexable: true,
    seo: {
      title: ENNEAGRAM_TYPES_DATA[8].title,
      description: ENNEAGRAM_TYPES_DATA[8].description
    }
  },
  {
    path: '*',
    component: NotFoundPage,
    publiclyIndexable: false,
    seo: {
      title: 'Page Not Found | FIVE LENS Enneagram',
      description: 'The requested page could not be found.'
    }
  }
];

export default routes;
